/**
 * Theme trial state — Royal Sapphire & Brass.
 *
 * Read/write to localStorage under `ibclub_theme_trial`. On mount, applies
 * the current theme by setting `<html data-theme="…">`. Consumers get a
 * hook + a `<ThemeTrialToggle />` floating pill.
 *
 * When theme is 'default' (or unset) the `data-theme` attribute is
 * removed from <html> so CSS overrides don't apply.
 */
import { useCallback, useEffect, useState } from 'react';
import { Palette, X } from 'lucide-react';

const STORAGE_KEY = 'ibclub_theme_trial';
const THEME_OPTIONS = [
  { id: 'default',        label: 'Default (Sapphire & Gold)' },
  { id: 'royal-sapphire', label: 'Royal Sapphire & Brass' },
];

const readStored = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return THEME_OPTIONS.some((t) => t.id === v) ? v : 'default';
  } catch {
    return 'default';
  }
};

const applyThemeToDocument = (id) => {
  const root = document.documentElement;
  if (id && id !== 'default') {
    root.setAttribute('data-theme', id);
  } else {
    root.removeAttribute('data-theme');
  }
};

export const useThemeTrial = () => {
  const [theme, setThemeState] = useState(readStored);

  const setTheme = useCallback((id) => {
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch { /* ignore */ }
    applyThemeToDocument(id);
    setThemeState(id);
  }, []);

  // Apply on mount + when it changes elsewhere in the app
  useEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  return { theme, setTheme, options: THEME_OPTIONS };
};

/**
 * Floating toggle pill — bottom-right, dismissable per-session.
 * Only surfaces the switcher; users can also change theme from /theme-preview.
 */
export const ThemeTrialToggle = () => {
  const { theme, setTheme, options } = useThemeTrial();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('ibclub_theme_pill_dismissed') === '1'; }
    catch { return false; }
  });
  const [open, setOpen] = useState(false);

  const dismiss = () => {
    try { sessionStorage.setItem('ibclub_theme_pill_dismissed', '1'); }
    catch { /* ignore */ }
    setDismissed(true);
  };

  // The pill is opt-in: only show when user has ever visited /theme-preview
  // OR when a theme is already active. This keeps it out of normal-visitor's
  // way. Escape hatch: ?theme=on in the URL forces it to show.
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const forceShow = params.get('theme') === 'on';
      const visited = localStorage.getItem('ibclub_theme_preview_visited') === '1';
      const active = theme && theme !== 'default';
      setEnabled(forceShow || visited || active);
    } catch { /* ignore */ }
  }, [theme]);

  if (!enabled || dismissed) return null;

  return (
    <div
      className="fixed z-[100] bottom-4 right-4"
      data-testid="theme-trial-toggle"
    >
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="theme-trial-toggle-open"
          className="inline-flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-wider bg-slate-900 border border-[#d4af37]/40 text-[#d4af37] hover:bg-slate-800 shadow-lg"
        >
          <Palette size={12} />
          Theme: {theme === 'default' ? 'Default' : 'Royal Sapphire'}
        </button>
      ) : (
        <div className="bg-slate-900 border border-[#d4af37]/40 p-4 min-w-[260px] shadow-2xl">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#d4af37] font-semibold inline-flex items-center gap-1.5">
              <Palette size={11} /> Theme trial
            </p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-slate-500 hover:text-white"
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>
          <p className="text-[11px] text-slate-400 mb-3 leading-relaxed">
            Try a live site-wide theme. Change applies only in your browser (localStorage) — other visitors are unaffected.
          </p>
          <div className="space-y-1.5">
            {options.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setTheme(opt.id)}
                data-testid={`theme-trial-set-${opt.id}`}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-xs text-left border transition-colors ${
                  theme === opt.id
                    ? 'bg-[#d4af37]/10 border-[#d4af37]/50 text-[#d4af37]'
                    : 'border-white/10 text-slate-300 hover:border-[#d4af37]/40'
                }`}
              >
                <span>{opt.label}</span>
                {theme === opt.id && <span className="text-[9px] uppercase tracking-wider">Active</span>}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="w-full mt-3 text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-300 py-1"
            data-testid="theme-trial-dismiss"
          >
            Dismiss pill for this session
          </button>
        </div>
      )}
    </div>
  );
};

export default useThemeTrial;
